import { contentful, Contentful } from 'api/contentful/api'

import { Block } from 'components/Block'
import { DigitalLegacyAbout } from 'components/digitalLegacy/digitalLegacyAbout/DigitalLegacyAbout'
import { DigitalLegacyAuthor } from 'components/digitalLegacy/digitalLegacyAuthor/DigitalLegacyAuthor'
import { DigitalLegacyHeader } from 'components/digitalLegacy/digitalLegacyHeader/DigitalLegacyHeader'
import { DigitalLegacyHero } from 'components/digitalLegacy/digitalLegacyHero/DigitalLegacyHero'

import { DigitalLegacyReview } from 'components/digitalLegacy/digitalLegacyReview/DigitalLegacyReview'
import { Footer } from 'components/footer/Footer'
import { Layout } from 'components/layout/Layout'
import { PageTitle } from 'components/PageTitle'
import { GetStaticPropsContext } from 'next'

import { Fragment, useRef } from 'react'
import { REVALIDATE_SECS } from 'utils/constants'
import { getIntlProps } from 'utils/i18n/getIntlProps'
import { Await } from 'utils/types'

export type DigitalLegacyHeaderButton = 'buy' | 'about' | 'author' | 'review'

export default function Page(props: { data: Await<Contentful<'getSingleDigitalLegacy'>> }) {
  const aboutRef = useRef<HTMLDivElement | null>(null)
  const authorRef = useRef<HTMLDivElement | null>(null)
  const reviewRef = useRef<HTMLDivElement | null>(null)

  const content = props.data.digitalLegacyCollection?.items?.[0]

  const handleClick = (type: DigitalLegacyHeaderButton) => {
    switch (type) {
      case 'buy': {
        content?.purchaseUrl && window.open(content.purchaseUrl, '_blank')
        break
      }
      case 'about': {
        aboutRef.current?.scrollIntoView({ behavior: 'smooth' })
        break
      }
      case 'author': {
        authorRef.current?.scrollIntoView({ behavior: 'smooth' })
        break
      }
      case 'review': {
        reviewRef.current?.scrollIntoView({ behavior: 'smooth' })
        break
      }
      default:
        break
    }
  }

  return (
    <Fragment>
      <PageTitle title="Digital Legacy" />
      <style jsx global>{`
        * {
          --digital-legacy-width: 1190px;
        }
      `}</style>
      <Layout
        header={<DigitalLegacyHeader onItemClick={handleClick} />}
        footer={<Footer variant="full" />}
      >
        <Block css={{ '--width': 'var(--digital-legacy-width)' }}>
          <main>
            <DigitalLegacyHero
              title={content?.headingTitle}
              description={content?.headingDescription}
              purchaseUrl={content?.purchaseUrl}
              viewReviewUrl={content?.viewReviewUrl}
            />

            <DigitalLegacyAbout
              ref={aboutRef}
              title={content?.aboutTitle}
              description={content?.aboutDescription}
              items={content?.topicListCollection?.items?.map((item) => ({
                content: item?.content,
              }))}
            />

            <DigitalLegacyAuthor
              ref={authorRef}
              title={content?.authorTitle}
              authors={content?.authorListCollection?.items?.map((item) => ({
                name: item?.name,
                photo: item?.photo?.url,
                description: item?.description,
                facebookUrl: item?.facebookUrl,
                twitterUrl: item?.twitterUrl,
                linkedinUrl: item?.linkedinUrl,
                instagramUrl: item?.instagramUrl,
              }))}
            />

            <DigitalLegacyReview
              ref={reviewRef}
              title={content?.reviewTitle}
              items={content?.reviewListCollection?.items?.map((item) => ({
                name: item?.name,
                description: item?.description,
                jobTitle: item?.jobTitle,
                photo: item?.photo?.url,
              }))}
              viewReviewUrl={content?.viewReviewUrl}
            />
          </main>
        </Block>
      </Layout>
    </Fragment>
  )
}

export async function getStaticProps(ctx: GetStaticPropsContext) {
  return {
    props: {
      ...(await getIntlProps(ctx)),
      data: await contentful.getSingleDigitalLegacy(),
    },
    revalidate: REVALIDATE_SECS,
  }
}
